import { type Result, success } from '@orus.eu/result'
import {
  type AnyLocalizableMessage,
  type KeyParameters,
  type Language,
  type ParameterlessTranslationKey,
  type TranslationKey,
  type TranslationKeyParameters,
  defaultLanguage,
  translate,
} from '@orus.eu/translations'

import { AbstractDimension, type DimensionValidationProblem, dimensionValidationFailure } from './abstract-dimension.ts'

/**
 * @deprecated use ExclusionV2 instead
 * ExclusionV1 is deprecated because it stores translated text in the DB and hence can never
 * be translated.
 */
export type ExclusionV1 = {
  name: string
  description: string
  /**
   * This field is added to prevent manually exclusions creation. Instead, use the newExclusion factory
   * function to enforce centralization of exclusion definitions, so we can document them in the backoffice
   */
  __brand: string
  type?: 'v1' | undefined
}

/**
 * @deprecated use ExclusionV3 instead
 * ExclusionV2 is deprecated because it does not support 'user typed' exlcusions.
 */
export type ExclusionV2 = {
  /**
   * Category of exclusion, meant to internally discriminate between different types of exclusions
   */
  name: string
  /**
   * Localizable message to explain the exclusion to the end user
   */
  message: AnyLocalizableMessage
  type?: 'v2' | undefined
}

export type ExclusionUserType = 'platform' | 'partner' | 'client' | 'default'

type ExclusionMessageByUserType =
  | {
      type: 'localizable'
      key: {
        default: TranslationKey | string
        partner?: TranslationKey | string
        platform?: TranslationKey | string
        client?: TranslationKey | string
      }
    }
  | {
      type: 'non-localizable'
      message: {
        default: string
        partner?: string
        platform?: string
        client?: string
      }
    }

/**
 * ExclusionV3 now supports 'user typed' exclusions.
 * We can configure a different message for each user environment (platform, partner, client)
 * Only default is mandatory, the other user types are optional.
 */
export type ExclusionV3 = {
  name: string
  type: 'v3'
  messageByUserType: ExclusionMessageByUserType
  parameters?: TranslationKeyParameters<TranslationKey>
}

export function getExclusionDescription(exclusion: Exclusion, userType: ExclusionUserType, language: Language): string {
  if (exclusion.type === 'v3') {
    const messageByUserType = exclusion.messageByUserType
    if (messageByUserType.type === 'localizable') {
      const parameters = exclusion.parameters ? (exclusion.parameters as KeyParameters[keyof KeyParameters]) : undefined

      const key = (
        userType in messageByUserType.key ? messageByUserType.key[userType] : messageByUserType.key.default
      ) as keyof KeyParameters | ParameterlessTranslationKey

      return parameters
        ? translate(key as keyof KeyParameters, language, parameters)
        : translate(key as ParameterlessTranslationKey, language)
    }

    return userType in messageByUserType.message
      ? messageByUserType.message[userType]!
      : messageByUserType.message.default
  }
  if (exclusion.type === 'v2' || 'message' in exclusion) {
    return translate(exclusion.message, language)
  }
  return exclusion.description
}

export type Exclusion = ExclusionV1 | ExclusionV2 | ExclusionV3

export class ExclusionDimension<NAME extends string> extends AbstractDimension<NAME, readonly Exclusion[]> {
  override formatToString(value: readonly Exclusion[], opts?: { language?: Language }): string | undefined {
    return value
      .map((exclusion) => getExclusionDescription(exclusion, 'default', opts?.language ?? defaultLanguage))
      .join(' | ')
  }
}

export class BypassedExclusionDimension<NAME extends string> extends AbstractDimension<NAME, readonly Exclusion[]> {
  override validateData(value: unknown): Result<Exclusion[], DimensionValidationProblem> {
    if (!Array.isArray(value)) {
      return dimensionValidationFailure(`Field ${this.name} is not an array`)
    }

    if (value.some((exclusion) => !('name' in exclusion) || typeof exclusion.name !== 'string')) {
      return dimensionValidationFailure(`Field ${this.name} does not have a valid exclusion name`)
    }

    return success(value)
  }

  override formatToString(value: readonly Exclusion[], opts?: { language?: Language }): string | undefined {
    return value
      .map((exclusion) => getExclusionDescription(exclusion, 'default', opts?.language ?? defaultLanguage))
      .join(' | ')
  }
}
